import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ComplianceReports = ({ onGenerateReport }) => {
  const [selectedReport, setSelectedReport] = useState('');
  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: ''
  });
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);

  const reportTypes = [
    {
      id: 'compliance-summary',
      name: 'Compliance Summary Report',
      description: 'Overall compliance status across all departments',
      icon: 'BarChart3'
    },
    {
      id: 'expiring-certifications',
      name: 'Expiring Certifications Report',
      description: 'Certifications expiring within specified timeframe',
      icon: 'Clock'
    },
    {
      id: 'training-effectiveness',
      name: 'Training Effectiveness Report',
      description: 'Analysis of training completion rates and trends',
      icon: 'TrendingUp'
    },
    {
      id: 'audit-trail',
      name: 'Blockchain Audit Trail',
      description: 'Immutable record of all certification activities',
      icon: 'Shield'
    },
    {
      id: 'department-breakdown',
      name: 'Department Breakdown Report',
      description: 'Detailed compliance metrics by department',
      icon: 'Users'
    },
    {
      id: 'regulatory-compliance',
      name: 'Regulatory Compliance Report',
      description: 'Compliance status for regulatory requirements',
      icon: 'FileCheck'
    }
  ];

  const departments = [
    'Operations',
    'Manufacturing',
    'Maintenance',
    'Quality Control',
    'Safety',
    'Administration',
    'Engineering',
    'Logistics'
  ];

  const recentReports = [
    {
      id: 1,
      name: 'Monthly Compliance Summary - September 2024',
      type: 'Compliance Summary',
      generatedDate: '2024-09-20',
      generatedBy: 'Sarah Johnson',
      status: 'completed',
      blockchainHash: '0xa1b2c3d4e5f6789012345678901234567890abcd'
    },
    {
      id: 2,
      name: 'Expiring Certifications - Q4 2024',
      type: 'Expiring Certifications',
      generatedDate: '2024-09-18',
      generatedBy: 'Mike Chen',
      status: 'completed',
      blockchainHash: '0xb2c3d4e5f6789012345678901234567890abcdef'
    },
    {
      id: 3,
      name: 'Department Breakdown - Manufacturing',
      type: 'Department Breakdown',
      generatedDate: '2024-09-15',
      generatedBy: 'Lisa Rodriguez',
      status: 'completed',
      blockchainHash: '0xc3d4e5f6789012345678901234567890abcdef12'
    }
  ];

  const handleDepartmentToggle = (department) => {
    setSelectedDepartments(prev => 
      prev?.includes(department)
        ? prev?.filter(d => d !== department)
        : [...prev, department]
    );
  };

  const handleGenerateReport = async () => {
    if (!selectedReport) return;

    setIsGenerating(true);
    
    // Simulate report generation
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    const reportData = {
      type: selectedReport,
      dateRange,
      departments: selectedDepartments,
      generatedAt: new Date()?.toISOString(),
      blockchainHash: `0x${Math.random()?.toString(16)?.substr(2, 40)}`
    };
    
    onGenerateReport(reportData);
    setIsGenerating(false);
  };

  return (
    <div className="space-y-6">
      {/* Report Generation */}
      <div className="bg-card rounded-lg border border-border p-6">
        <h2 className="text-xl font-semibold text-foreground mb-6">Generate Compliance Report</h2>
        
        <div className="space-y-6">
          {/* Report Type Selection */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-3">
              Select Report Type
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {reportTypes?.map((report) => (
                <button
                  key={report?.id}
                  onClick={() => setSelectedReport(report?.id)}
                  className={`p-4 rounded-lg border text-left transition-clinical ${
                    selectedReport === report?.id
                      ? 'border-primary bg-primary/5 text-primary' :'border-border hover:border-primary/50 text-foreground'
                  }`}
                >
                  <div className="flex items-start space-x-3">
                    <Icon name={report?.icon} size={20} />
                    <div>
                      <h3 className="font-medium">{report?.name}</h3>
                      <p className="text-xs text-muted-foreground mt-1">
                        {report?.description}
                      </p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Date Range */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Start Date
              </label>
              <input
                type="date"
                value={dateRange?.startDate}
                onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e?.target?.value }))}
                className="w-full px-3 py-2 bg-input border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                End Date
              </label>
              <input
                type="date"
                value={dateRange?.endDate}
                onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e?.target?.value }))}
                className="w-full px-3 py-2 bg-input border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
          </div>

          {/* Department Selection */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-3">
              Select Departments (Optional)
            </label>
            <div className="flex flex-wrap gap-2">
              {departments?.map((department) => (
                <button
                  key={department}
                  onClick={() => handleDepartmentToggle(department)}
                  className={`px-3 py-1 rounded-full text-sm font-medium transition-clinical ${
                    selectedDepartments?.includes(department)
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-muted-foreground hover:bg-primary/10 hover:text-primary'
                  }`}
                >
                  {department}
                </button>
              ))}
            </div>
          </div>

          {/* Generate Button */}
          <div className="flex items-center justify-between pt-4 border-t border-border">
            <div className="text-sm text-muted-foreground">
              Reports are blockchain-anchored for immutable audit trails
            </div>
            <Button
              onClick={handleGenerateReport}
              disabled={!selectedReport || isGenerating}
              loading={isGenerating}
              iconName="FileText"
              iconPosition="left"
            >
              Generate Report
            </Button>
          </div>
        </div>
      </div>
      {/* Recent Reports */}
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-foreground">Recent Reports</h2>
          <Button variant="outline" iconName="Archive" iconPosition="left">
            View All Reports
          </Button>
        </div>

        <div className="space-y-4">
          {recentReports?.map((report) => (
            <div key={report?.id} className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
              <div className="flex items-center space-x-4">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name="FileText" size={20} className="text-primary" />
                </div>
                <div>
                  <h3 className="font-medium text-foreground">{report?.name}</h3>
                  <p className="text-sm text-muted-foreground">
                    Generated by {report?.generatedBy} on {report?.generatedDate}
                  </p>
                  <div className="flex items-center space-x-2 mt-1">
                    <Icon name="Shield" size={12} className="text-success" />
                    <span className="text-xs text-success font-mono">
                      {report?.blockchainHash?.substring(0, 16)}...
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Button variant="ghost" size="sm" iconName="Eye">
                  View
                </Button>
                <Button variant="ghost" size="sm" iconName="Download">
                  Download
                </Button>
                <Button variant="ghost" size="sm" iconName="Share">
                  Share
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ComplianceReports;